import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
const MAX_RETRIES = 4;
const RETRY_DELAY = 2000; // 2 seconds between retries
const DocumentViewer = ({ fileExtension, isVideo, ...props }) => {
    const [loading, setLoading] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [iframeKey, setIframeKey] = useState(Date.now());
    const [isRetrying, setIsRetrying] = useState(false);
    const getViewerUrl = useCallback(() => {
        const sourceUrl = props.type === 'direct' ? props.url : props.fileUrl;
        if (props.type === 'direct') {
            return sourceUrl;
        }
        if (fileExtension === 'pdf') {
            return sourceUrl;
        }
        else if (fileExtension === 'docx') {
            const uniqueId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const encodedUrl = encodeURIComponent(sourceUrl);
            return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true&r=${uniqueId}`;
        }
        return sourceUrl;
    }, [props, fileExtension]);
    const handleRetry = useCallback(() => {
        if (!isRetrying && retryCount < MAX_RETRIES) {
            setIsRetrying(true);
            setLoading(true);
            setTimeout(() => {
                setRetryCount(prev => prev + 1);
                setIframeKey(Date.now());
                setIsRetrying(false);
            }, RETRY_DELAY);
        }
    }, [isRetrying, retryCount]);
    const handleLoad = useCallback(() => {
        setLoading(false);
        setIsRetrying(false);
    }, []);
    const handleError = useCallback(() => {
        if (!isRetrying && retryCount < MAX_RETRIES) {
            handleRetry();
        }
        else {
            setLoading(false);
            setIsRetrying(false);
        }
    }, [retryCount, handleRetry, isRetrying]);
    // Reset states when URL changes
    useEffect(() => {
        setRetryCount(0);
        setLoading(true);
        setIsRetrying(false);
        setIframeKey(Date.now());
    }, [props.type === 'direct' ? props.url : props.fileUrl]);
    // Timeout handler
    useEffect(() => {
        if (!loading)
            return;
        const timeoutId = setTimeout(() => {
            if (loading && !isRetrying && retryCount < MAX_RETRIES) {
                handleRetry();
            }
        }, 10000); // 10 seconds timeout
        return () => clearTimeout(timeoutId);
    }, [loading, isRetrying, handleRetry, retryCount]);
    const viewerUrl = getViewerUrl();
    return (_jsx("div", { className: "w-full h-screen flex flex-col", children: _jsxs("div", { className: "relative w-full h-full bg-gray-100 rounded-lg shadow-md overflow-hidden", children: [loading && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gray-50", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" }) })), _jsx("iframe", { src: viewerUrl, className: `w-full border-none outline-none bg-none ${isVideo ? "aspect-video h-auto" : "aspect-auto h-full"}`, onLoad: handleLoad, onError: handleError, title: "Document Viewer", frameBorder: "0", allow: "fullscreen" }, iframeKey)] }) }));
};
export default DocumentViewer;
